import React, { useState } from 'react';
import styled from '@emotion/styled';
import Tabs from 'ui/modules/tabs/Tabs';
import Tab from 'ui/modules/tabs/Tab';
import Button from 'ui/elements/buttons/Button';
import BookmarkIcon from 'ui/elements/icons/BookmarkIcon';
import NumberIndicator from 'ui/elements/Indicator/NumberIndicator';
import useLazyResource from 'util/resource/useLazyResource';
import useNotify from 'hooks/useNotify';
import { usersApi } from 'apis/CompanyAPI/users/usersApi';
import { ShortlistCreateDialog } from 'pages/Shortlists/ShortlistDialog';
import { Shortlist } from 'types/user';
import { userUrls } from 'urls';
import { distinct } from 'util/arrayUtils';
import useCollectionShortlists from './useCollectionShortlists';

interface Props {
  shortlists: Shortlist[];
  activeShortlistId?: UUID;
  setShortlists: ReturnType<typeof useCollectionShortlists>['setShortlists'];
}

const ALL = 'all';

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`;

const TabsContainer = styled.div`
  flex: 1;
  min-width: 0;
`;

const NoWrap = styled.div`
  flex-shrink: 0;
  white-space: nowrap;
`;

/** "All companies" and one tab per list, with a button for a new list. Shared by the hub and the list pages so it stays put when switching. */
export default function CollectionTabs({ shortlists, activeShortlistId, setShortlists }: Props) {
  const notify = useNotify();
  const [isCreateDialogOpen, setCreateDialogOpen] = useState(false);
  const companyCount = distinct(shortlists.flatMap(list => list.companies.map(company => company.id))).length;

  const [createList, isCreatingList] = useLazyResource(
    (payload: { listName: string; description?: string }) =>
      usersApi.shortlists.create(payload.listName, payload.description),
    {
      onSuccess: newShortlist => {
        setShortlists({ values: [newShortlist, ...shortlists] }, { revalidate: false });
        setCreateDialogOpen(false);
        notify('success', 'List created');
      },
      onFailure: message => notify('error', message ?? 'Could not save the list'),
    },
  );

  return (
    <Row className="u-content-spacing-bottom">
      <TabsContainer>
        <Tabs activeTab={activeShortlistId ?? ALL} type="rounded" variant="scrollable">
          <Tab
            value={ALL}
            href={userUrls.collections.list}
            label={<TabLabel name="All companies" count={companyCount} isActive={!activeShortlistId} />}
          />
          {shortlists.map(list => (
            <Tab
              key={list.id}
              value={list.id}
              href={userUrls.collections.view(list.id)}
              label={
                <TabLabel name={list.name} count={list.companies.length} isActive={list.id === activeShortlistId} />
              }
            />
          ))}
        </Tabs>
      </TabsContainer>
      <NoWrap>
        <Button kind="tertiary" onClick={() => setCreateDialogOpen(true)}>
          <BookmarkIcon fontSize="small" className="u-quarter-spacing-right" />
          New list
        </Button>
      </NoWrap>
      {isCreateDialogOpen && (
        <ShortlistCreateDialog
          dialogHandler={{
            isOpen: isCreateDialogOpen,
            open: () => setCreateDialogOpen(true),
            close: () => setCreateDialogOpen(false),
          }}
          onSubmit={(listName, description) => createList({ listName, description })}
          isSaving={isCreatingList}
        />
      )}
    </Row>
  );
}

function TabLabel({ name, count, isActive }: { name: string; count: number; isActive: boolean }) {
  return (
    <span className="u-flex u-flex-align-center u-flex--gap-quarter">
      {name}
      <NumberIndicator value={count} color={isActive ? 'blue' : 'grey'} />
    </span>
  );
}
